import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

export type SPTheme = 'light' | 'dark';

interface SPThemeValue {
  theme: SPTheme;
  setTheme: (t: SPTheme) => void;
  toggle: () => void;
  /** Class string for a portalled root so `--sp-*` tokens resolve in the current theme. */
  portalClass: string;
}

const SPThemeContext = createContext<SPThemeValue | null>(null);

const STORAGE_KEY = 'spJourneyTheme';

function readInitial(preferred?: SPTheme): SPTheme {
  if (preferred) return preferred;
  if (typeof window === 'undefined') return 'light';
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === 'light' || saved === 'dark') return saved;
  } catch { /* ignore */ }
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/**
 * Theme scope for the Student Journey. Kept deliberately separate from the
 * CLASSIC portal's `StudentThemeContext` (key `studentPortalTheme`) so the two
 * never interfere. The JourneyShell wraps its subtree in this; portalled
 * primitives (SPModal, future toasts/popovers) read `portalClass` so their
 * detached DOM still themes correctly.
 */
export function SPThemeProvider({
  children,
  value,
  defaultTheme,
}: {
  children: React.ReactNode;
  /** Controlled mode — drive the theme from outside (e.g. bridged to StudentThemeContext). */
  value?: SPTheme;
  defaultTheme?: SPTheme;
}) {
  const [internal, setInternal] = useState<SPTheme>(() => readInitial(defaultTheme));
  const theme = value ?? internal;

  const setTheme = useCallback(
    (t: SPTheme) => {
      setInternal(t);
      try { localStorage.setItem(STORAGE_KEY, t); } catch { /* ignore */ }
    },
    [],
  );

  useEffect(() => {
    if (value) {
      try { localStorage.setItem(STORAGE_KEY, value); } catch { /* ignore */ }
    }
  }, [value]);

  const ctx = useMemo<SPThemeValue>(
    () => ({
      theme,
      setTheme,
      toggle: () => setTheme(theme === 'dark' ? 'light' : 'dark'),
      portalClass: theme === 'dark' ? 'sp-journey sp-theme-dark' : 'sp-journey',
    }),
    [theme, setTheme],
  );

  return <SPThemeContext.Provider value={ctx}>{children}</SPThemeContext.Provider>;
}

/** Read the Journey theme. Falls back to a light, no-op value when used outside a provider. */
export function useSPTheme(): SPThemeValue {
  return (
    useContext(SPThemeContext) ?? {
      theme: 'light',
      setTheme: () => {},
      toggle: () => {},
      portalClass: 'sp-journey',
    }
  );
}
